import React, { createContext, useContext, useState, useEffect } from "react";
import { AuthContext } from "../../../context/authcontext";
import { API_BASE_URL } from "../../../environment";
import { getAuthHeaders } from "../../../utils/authUtils";

interface ContractFormDataContextType {
  teams: any[];
  contractTypes: any[];
  proposals: any[];
  loading: boolean;
  error: string | null;
}

const ContractFormDataContext = createContext<
  ContractFormDataContextType | undefined
>(undefined);

interface ContractFormDataProviderProps {
  children: React.ReactNode;
  isMainApiCompleted?: boolean;
  shouldLoad?: boolean;
  loadTypesOnly?: boolean;
}

export const ContractFormDataProvider: React.FC<
  ContractFormDataProviderProps
> = ({ children, isMainApiCompleted = false, shouldLoad = true, loadTypesOnly = false }) => {
  const { selectedTenantId } = useContext(AuthContext);
  const [teams, setTeams] = useState<any[]>([]);
  const [contractTypes, setContractTypes] = useState<any[]>([]);
  const [proposals, setProposals] = useState<any[]>([]);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [typesLoaded, setTypesLoaded] = useState(false);
  const [fullDataLoaded, setFullDataLoaded] = useState(false);

  useEffect(() => {
    if (!selectedTenantId || !isMainApiCompleted) return;

    if (shouldLoad && !fullDataLoaded) {
      setLoading(true);
      setError(null);

      const fetchFormData = async () => {
        try {
          const authHeaders = getAuthHeaders();
          const promises = [
            fetch(`${API_BASE_URL}/team/?tenant_id=${selectedTenantId}&response_mode=light`, {
              headers: authHeaders,
            }),

            fetch(
              `${API_BASE_URL}/contract-type/?tenant_id=${selectedTenantId}&response_mode=light`,
              {
                headers: authHeaders,
              }
            ),

            fetch(`${API_BASE_URL}/proposal/?tenant_id=${selectedTenantId}&is_archive=false&response_mode=light`, {
              headers: authHeaders,
            }),
          ];

          const responses = await Promise.all(promises);
          const results = await Promise.all(
            responses.map((response) => {
              if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
              }
              return response.json();
            })
          );

          setTeams(results[0].results || []);

          const sortedContractTypes = (results[1].results || []).sort(
            (a: any, b: any) => a.contract_type_order - b.contract_type_order
          );
          setContractTypes(sortedContractTypes);
          setProposals(results[2].results || []);
          setTypesLoaded(true);
          setFullDataLoaded(true);
        } catch (err) {
          console.error("Error fetching contract form data:", err);
          setError(
            err instanceof Error ? err.message : "Failed to fetch form data"
          );
        } finally {
          setLoading(false);
        }
      };

      fetchFormData();
    } else if (loadTypesOnly && !typesLoaded && !fullDataLoaded) {
      setLoading(true);
      setError(null);

      const fetchTypes = async () => {
        try {
          const response = await fetch(
            `${API_BASE_URL}/contract-type/?tenant_id=${selectedTenantId}&response_mode=light`,
            { headers: getAuthHeaders() }
          );
          if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
          }
          const data = await response.json();
          const sortedContractTypes = (data.results || []).sort(
            (a: any, b: any) => a.contract_type_order - b.contract_type_order
          );
          setContractTypes(sortedContractTypes);
          setTypesLoaded(true);
        } catch (err) {
          console.error("Error fetching contract types:", err);
          setError(
            err instanceof Error ? err.message : "Failed to fetch contract types"
          );
        } finally {
          setLoading(false);
        }
      };

      fetchTypes();
    }
  }, [selectedTenantId, isMainApiCompleted, shouldLoad, loadTypesOnly, fullDataLoaded, typesLoaded]);

  const value: ContractFormDataContextType = {
    teams,
    contractTypes,
    proposals,

    loading,
    error,
  };

  return (
    <ContractFormDataContext.Provider value={value}>
      {children}
    </ContractFormDataContext.Provider>
  );
};

export const useContractFormData = () => {
  const context = useContext(ContractFormDataContext);
  if (context === undefined) {
    throw new Error(
      "useContractFormData must be used within a ContractFormDataProvider"
    );
  }
  return context;
};
